import { getSharedPostData } from "../../utils/sharedPost";
import { isAudioMessage } from "../../utils/chatAudio";
import { normalizeMessageContent } from "./messageCode";

export const idsMatch = (left, right) => {
  if (
    left === null ||
    left === undefined ||
    right === null ||
    right === undefined
  ) {
    return false;
  }

  return String(left) === String(right);
};

export const getConversationStatus = (conversation, t) => {
  const status = String(conversation.status || "");
  if (["online", "offline"].includes(status))
    return t(`chat.${status}`, status);
  const groupCount = status.match(/^(\d+)\s+members?$/i);
  if (
    conversation.type === "GROUP" &&
    (conversation.membersCount || groupCount)
  ) {
    return t("chat.memberCount", {
      count: Number(conversation.membersCount || groupCount[1]),
      defaultValue: "Members: {{count}}",
    });
  }
  return status;
};

export const getSelectedConversation = (chats, selectedConversationId) =>
  chats.find((conversation) =>
    idsMatch(conversation.id, selectedConversationId),
  ) || null;

export const filterConversations = (chats, filter, searchValue) => {
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  return chats.filter((conversation) => {
    const matchesFilter =
      filter === "all"
        ? true
        : filter === "groups"
          ? conversation.type === "GROUP"
          : conversation.type !== "GROUP";

    const matchesSearch =
      normalizedSearchValue.length === 0
        ? true
        : conversation.name.toLowerCase().includes(normalizedSearchValue) ||
          conversation.preview.toLowerCase().includes(normalizedSearchValue);

    return matchesFilter && matchesSearch;
  });
};

export const getEndpointItems = (endpointSummary) =>
  Object.entries(endpointSummary);

const isImageAttachment = (message) => {
  const normalizedFileType = String(
    message?.file_type || message?.fileType || "",
  )
    .trim()
    .toLowerCase();

  return (
    String(message?.type || "").toUpperCase() === "IMAGE" ||
    normalizedFileType.startsWith("image")
  );
};

export const isImagePlaceholderText = (value) =>
  /^\[?\s*image\s*\]?$/i.test(String(value || "").trim());

export const getMessageDisplayText = (message) => {
  const shared = getSharedPostData(message);
  if (shared) return shared.optionalMessage;
  const text = message?.text || message?.content || "";
  if (isAudioMessage(message) && /^voice message$/i.test(text.trim()))
    return "";
  const fileName = message?.file_name || message?.fileName;
  // Older clients copied the attachment name into content as a placeholder.
  // The file card already displays it; keep only a distinct, user-written caption.
  if (fileName && text.trim() === fileName.trim()) return "";

  return isImageAttachment(message) && isImagePlaceholderText(text) ? "" : text;
};

export const getReplyPreviewText = (
  message,
  t = (_key, fallback) => fallback,
) => {
  const preview = message?.reply_preview || message?.replyPreview;
  const text = preview?.text || preview?.content || "";

  if (isImageAttachment(preview) || isImagePlaceholderText(text)) {
    return t("chat.photo", "Photo");
  }

  return text;
};

export const getMessagePreviewText = (
  message,
  t = (_key, fallback) => fallback,
) => {
  if (!message) {
    return "";
  }
  const shared = getSharedPostData(message);
  if (shared)
    return shared.optionalMessage || t("profilePage.sharedPost", "Shared post");

  const normalizedFileType = String(message.file_type || message.fileType || "")
    .trim()
    .toLowerCase();
  const hasImageAttachment = isImageAttachment(message);
  const isDocumentAttachment =
    message.type === "DOCUMENT" ||
    normalizedFileType === "document" ||
    normalizedFileType.startsWith("application") ||
    (message.type === "FILE" && Boolean(message.file_url || message.fileUrl));
  const text = message.text?.trim() || message.content?.trim() || "";

  if (isAudioMessage(message)) {
    return t("VOICE_MESSAGE", "Voice message");
  }

  if (hasImageAttachment) {
    return t("chat.photo", "Photo");
  }

  if (message.file_name) {
    return message.file_name;
  }

  if (isDocumentAttachment) {
    return t("chat.documentAttachment", "Document attachment");
  }

  if (text) {
    return text;
  }

  return "";
};

export const createMessagePayload = (
  selectedConversation,
  composerText,
  attachment = null,
  replyToMessage = null,
  currentUsername = "",
) => {
  const messageContent = normalizeMessageContent(composerText);
  const attachmentKind = attachment?.kind || null;
  const hasAttachment = Boolean(attachment);
  const isAudioAttachment = attachmentKind === "audio";
  const fileType = attachment?.file?.type || "";

  return {
    chat_id: selectedConversation.id,
    content: messageContent || (isAudioAttachment ? "Voice message" : ""),
    type: hasAttachment
      ? attachmentKind === "image"
        ? "IMAGE"
        : isAudioAttachment
          ? "AUDIO"
          : "FILE"
      : "TEXT",
    file_url: attachment?.previewUrl || null,
    attachmentFile: attachment?.file || null,
    file_name: attachment?.file?.name || null,
    file_type:
      attachmentKind === "audio"
        ? "audio"
        : attachmentKind === "image"
          ? "image"
          : attachmentKind === "document"
            ? "document"
            : fileType || "",
    file_size: attachment?.file?.size || null,
    duration_ms: attachment?.durationMs || null,
    reply_to_id: replyToMessage?.id || null,
    reply_preview: replyToMessage
      ? {
          id: replyToMessage.id,
          author: replyToMessage.author || selectedConversation.name,
          text: getMessagePreviewText(replyToMessage),
        }
      : null,
    s_username: currentUsername,
    r_username: selectedConversation.peerUsername || selectedConversation.name,
  };
};

export const getConversationSearchResults = (messages, searchValue) => {
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  return messages.filter((message) => {
    if (!normalizedSearchValue) {
      return true;
    }

    const searchableText = [
      message.author,
      message.text,
      message.content,
      message.file_name,
      message.file_type,
      message.type,
      message.time,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedSearchValue);
  });
};
